import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useIntl } from 'react-intl';
import { commonMessages } from 'utils/commonMessages';
import { FindServiceCallByContractQuery, ServiceCall } from 'modules/service-call/types';
import { useForm } from 'react-hook-form';
import { ServiceCallsApi } from 'modules/service-call/api';
import { useMutation } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import { ServiceCallsResultDialog } from './ServiceCallsResultDialog';

interface AccordionWithClientContractNumberProps {
  expanded: boolean,
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void
}

export const AccordionWithClientContractNumber: React.FC<AccordionWithClientContractNumberProps> = ({ expanded, onChange }) => {
  const { formatMessage } = useIntl();
  const [isDialogResultOpen, setIsDialogResultOpen] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<FindServiceCallByContractQuery>();
  const [serviceCalls, setServiceCalls] = useState<ServiceCall[]>([]);

  const findMutation = useMutation(ServiceCallsApi.findByContract, {
    onSuccess: (result) => {
      setServiceCalls(result);
    },
    onError: () => {
      setServiceCalls([]);
    },
    onSettled: () => {
      setIsDialogResultOpen(true);
    }
  });

  const onSubmit = (data: FindServiceCallByContractQuery) => {
    findMutation.mutate(data);
  };

  const handleCloseDialog = () => {
    setIsDialogResultOpen(false);
  };

  const isSubmitting = findMutation.isLoading;

  return (
    <Accordion expanded={expanded} onChange={onChange}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-withlclient-contract-number-content"
        id="panel-withlclient-contract-number-header"
      >
        <Typography>{formatMessage({ id: 'FindServiceCall.WithClientContractNumber', defaultMessage: 'With your client and contract number' })}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack gap={2}>
            <TextField
              {...register('contractNumber', { required: formatMessage(commonMessages.fieldIsRequired) })}
              required
              fullWidth
              label={formatMessage(commonMessages.contractNumber)}
              variant="outlined"
              autoComplete='contractNumber'
              error={Boolean(errors.contractNumber)}
              helperText={errors.contractNumber?.message}
              disabled={isSubmitting}
            />
            <TextField
              {...register('clientNumber', { required: formatMessage(commonMessages.fieldIsRequired) })}
              required
              fullWidth
              label={formatMessage(commonMessages.clientNumber)}
              variant="outlined"
              autoComplete='clientNumber'
              error={Boolean(errors.clientNumber)}
              helperText={errors.clientNumber?.message}
              disabled={isSubmitting}
            />
            <Stack direction='row' justifyContent='end'>
              <LoadingButton variant="contained" color='secondary' type='submit' loading={isSubmitting} >{formatMessage({ id: 'FindServiceCall.FindButton', defaultMessage: 'Find' })}</LoadingButton>
            </Stack>
          </Stack>
        </form>
      </AccordionDetails>
      <ServiceCallsResultDialog isOpen={isDialogResultOpen} onClose={handleCloseDialog} serviceCalls={serviceCalls} />
    </Accordion>
  );
};